import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import API_URL from '../config';
import { MAX_ATTACHMENTS, validateFileSelection } from '../utils/fileUploadValidation';
import ToastMessage from './ToastMessage';
import useToast from '../hooks/useToast';
import { useLanguage } from '../context/LanguageContext';
import { getStatusBadgeClass } from '../utils/statusBadge';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, handleExpiredSession } from '../utils/authSession';
import {
  addLocalDevNotification,
  getLocalDevSubtasks,
  isLocalDevCompanyId,
  updateLocalDevSubtask,
} from '../utils/localDevAuth';

const MyTasks = ({ user }) => {
  const { t, locale } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [submissionData, setSubmissionData] = useState({
    submitted_files: [],
    submitted_links: [],
  });
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast, showToast, clearToast } = useToast();
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem('company_id'));
  const effectiveCompanyId = user?.company?.id || localStorage.getItem('company_id') || '1';
  const normalizeUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${API_URL}${url}`;
  };

  const fetchTasks = useCallback(async () => {
    try {
      const token = getStoredAccessToken();
      if (isLocalDevSession) {
        const localTasks = getLocalDevSubtasks()
          .filter(task => task.major_task?.project?.company?.id === user.company?.id);
        if (user.role === 'team_member') {
          setTasks(localTasks.filter(task => task.assigned_to?.id === user.id));
          setPendingApprovals([]);
        } else if (user.role === 'manager') {
          setTasks(localTasks.filter(task => task.assigned_by?.id === user.id || task.assigned_to?.id === user.id));
          setPendingApprovals(localTasks.filter(task => task.status === 'pending_approval'));
        }
        setError('');
        return;
      }
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      let tasksRes, pendingRes;
      const config = { headers: getAuthHeaders(token) };
      if (user.role === 'team_member') {
        tasksRes = await axios.get(`${API_URL}/api/subtasks/`, config);
        const filteredTasks = tasksRes.data.filter(task => task.assigned_to?.id === user.id);
        const tasksWithDetails = await Promise.all(filteredTasks.map(async (task) => {
          try {
            const majorTaskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, config);
            return { ...task, major_task: majorTaskRes.data };
          } catch (error) {
            console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
            return task;
          }
        }));
        setTasks(tasksWithDetails);
      } else if (user.role === 'manager') {
        [tasksRes, pendingRes] = await Promise.all([
          axios.get(`${API_URL}/api/subtasks/`, config),
          axios.get(`${API_URL}/api/pending-approvals/`, config),
        ]);
        const filteredTasks = tasksRes.data.filter(task => task.assigned_by?.id === user.id);
        const tasksWithDetails = await Promise.all(filteredTasks.map(async (task) => {
          try {
            const majorTaskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, config);
            return { ...task, major_task: majorTaskRes.data };
          } catch (error) {
            console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
            return task;
          }
        }));
        setTasks(tasksWithDetails);
        const pendingWithDetails = await Promise.all(pendingRes.data.map(async (task) => {
          try {
            const majorTaskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, config);
            return { ...task, major_task: majorTaskRes.data };
          } catch (error) {
            console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
            return task;
          }
        }));
        setPendingApprovals(pendingWithDetails);
      }
      setError('');
    } catch (error) {
      console.error('Error fetching tasks:', error);
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: 'Failed to load tasks. Please try again.',
      }));
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyId, isLocalDevSession, user, navigate]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      if (isLocalDevSession) {
        updateLocalDevSubtask(taskId, { status: newStatus });
        setSuccess('Task status updated successfully!');
        setError('');
        fetchTasks();
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const formData = new FormData();
      formData.append('status', newStatus);

      await axios.patch(
        `${API_URL}/api/subtasks/${taskId}/`,
        formData,
        {
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setSuccess('Task status updated successfully!');
      setError('');
      fetchTasks();
    } catch (error) {
      console.error('Error updating status:', error);
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: 'Failed to update task status.',
      }));
      setSuccess('');
    }
  };

  const openSubmissionModal = (taskId) => {
    setSelectedTaskId(taskId);
    setSubmissionData({ submitted_files: [], submitted_links: [] });
    setUploadSuccess(false);
    setSubmissionModalOpen(true);
  };

  const handleSubmissionInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'submitted_files') {
      const incomingFiles = Array.from(files || []);
      setSubmissionData(prev => {
        const validation = validateFileSelection(prev.submitted_files, incomingFiles);
        if (!validation.isValid) {
          showToast(validation.error, 'error');
          return prev;
        }
        setError('');
        return { ...prev, submitted_files: validation.files };
      });
      e.target.value = '';
      return;
    }
    setSubmissionData(prev => ({ ...prev, [name]: value }));
  };

  const removeSubmissionFile = (index) => {
    setSubmissionData(prev => ({
      ...prev,
      submitted_files: prev.submitted_files.filter((_, i) => i !== index),
    }));
  };

  const addSubmissionLink = () => {
    setSubmissionData(prev => {
      if (prev.submitted_links.length >= MAX_ATTACHMENTS) {
        setError('Maximum 5 links allowed.');
        return prev;
      }
      setError('');
      return { ...prev, submitted_links: [...prev.submitted_links, ''] };
    });
  };

  const updateSubmissionLink = (index, value) => {
    setSubmissionData(prev => ({
      ...prev,
      submitted_links: prev.submitted_links.map((link, i) => (i === index ? value : link)),
    }));
  };

  const removeSubmissionLink = (index) => {
    setSubmissionData(prev => ({
      ...prev,
      submitted_links: prev.submitted_links.filter((_, i) => i !== index),
    }));
  };

  const handleSendForApproval = async () => {
    if (!selectedTaskId) return;
    try {
      setUploading(true);
      if (isLocalDevSession) {
        const updatedTask = updateLocalDevSubtask(selectedTaskId, {
          status: 'pending_approval',
          submitted_links: submissionData.submitted_links.filter(link => link && link.trim()),
        });
        if (updatedTask?.assigned_by?.email) {
          addLocalDevNotification({
            recipientEmail: updatedTask.assigned_by.email,
            type: 'task_pending_approval',
            message: `${user.name || user.email} has submitted the task '${updatedTask.name}' for your approval.`,
            messageHi: `कार्य '${updatedTask.name}' स्वीकृति के लिए जमा किया गया है।`,
            createdBy: user.email,
          });
        }
        setSuccess('Task sent for approval!');
        setError('');
        setUploadSuccess(true);
        setSubmissionModalOpen(false);
        window.dispatchEvent(new Event('notifications:refresh'));
        fetchTasks();
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const formData = new FormData();
      formData.append('status', 'pending_approval');
      if (submissionData.submitted_files && submissionData.submitted_files.length > 0) {
        submissionData.submitted_files.forEach(file => {
          formData.append('submitted_files', file);
        });
      }
      if (submissionData.submitted_links && submissionData.submitted_links.length > 0) {
        submissionData.submitted_links.forEach(link => {
          if (link) {
            formData.append('submitted_links', link);
          }
        });
      }

      await axios.patch(
        `${API_URL}/api/subtasks/${selectedTaskId}/`,
        formData,
        {
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setSuccess('Task sent for approval!');
      setError('');
      setUploadSuccess(true);
      setSubmissionModalOpen(false);
      window.dispatchEvent(new Event('notifications:refresh'));
      fetchTasks();
    } catch (error) {
      console.error('Error sending for approval:', error);
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: 'Failed to send task for approval.',
      }));
      setSuccess('');
    } finally {
      setUploading(false);
    }
  };

  const handleDeclineTask = async (taskId) => {
    try {
      if (isLocalDevSession) {
        updateLocalDevSubtask(taskId, { status: 'ongoing' });
        setSuccess('Task declined successfully!');
        setError('');
        fetchTasks();
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const formData = new FormData();
      formData.append('status', 'ongoing');

      await axios.patch(
        `${API_URL}/api/subtasks/${taskId}/`,
        formData,
        {
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setSuccess('Task declined successfully!');
      setError('');
      fetchTasks();
    } catch (error) {
      console.error('Error declining task:', error);
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: 'Failed to decline task.',
      }));
      setSuccess('');
    }
  };

  const filteredTasks = tasks.filter(task =>
    task.name.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => new Date(b.due_date) - new Date(a.due_date));
  
  const sortedPendingApprovals = [...pendingApprovals].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  if (loading) {
    return <p className="text-text italic text-sm sm:text-base">{t('common.loading')}</p>;
  }

  return (
    <>
      <ToastMessage toast={toast} onClose={clearToast} />
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
        {t('common.myTasks')} - Welcome {user.code || (user.role === 'manager' ? 'Manager' : 'Team Member')}
      </h1>
      {error && <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>}
      {success && <p className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base">{success}</p>}
      <div className="mb-4 sm:mb-6">
        <input
          type="text"
          placeholder="Search tasks..."
          className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="card p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Assigned Tasks</h2>
        {filteredTasks.length === 0 ? (
          <p className="text-text italic text-sm sm:text-base">No tasks assigned.</p>
        ) : (
          <div className="admin-table-shell overflow-x-auto">
            <table className="admin-table text-sm sm:text-base">
              <thead>
                <tr className="soft-table-head">
                  <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Title</th>
                  <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Project</th>
                  <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Major Task</th>
                  <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Due Date & Time</th>
                  <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Priority</th>
                  <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Attachments</th>
                  <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Status</th>
                  <th className="p-2 sm:p-3 min-w-[200px] sm:min-w-[240px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => {
                  const projectStatus = task.major_task?.project?.status;
                  const isProjectCompleted = projectStatus === 'completed';
                  const assignedFiles = (task.assigned_files && task.assigned_files.length > 0)
                    ? task.assigned_files
                    : (task.assigned_file ? [task.assigned_file] : []);
                  const assignedLinks = (task.assigned_links && task.assigned_links.length > 0)
                    ? task.assigned_links
                    : (task.assigned_link ? [task.assigned_link] : []);
                  return (
                    <tr key={task.id} className="border-b border-secondary table-row-hover">
                      <td className="p-2 sm:p-3 text-text">{task.name}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.name || 'N/A'}</td>
                      <td className="p-2 sm:p-3 text-text">
                        {task.due_date ? formatDateTime24(task.due_date, locale) : 'N/A'}
                      </td>
                      <td className="p-2 sm:p-3 text-text">{task.priority}</td>
                      <td className="p-2 sm:p-3 text-text">
                        {assignedFiles.map((fileUrl, index) => (
                          <a
                            key={`assigned-file-${task.id}-${index}`}
                            href={normalizeUrl(fileUrl)}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline block"
                          >
                            Download File {index + 1}
                          </a>
                        ))}
                        {assignedLinks.map((linkUrl, index) => (
                          <a
                            key={`assigned-link-${task.id}-${index}`}
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline block"
                          >
                            View Link {index + 1}
                          </a>
                        ))}
                        {assignedFiles.length === 0 && assignedLinks.length === 0 && '-'}
                      </td>
                      <td className="p-2 sm:p-3 text-text">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                      </td>
                      <td className="p-2 sm:p-3 text-text min-w-[200px] sm:min-w-[240px]">
                        {user.role === 'team_member' && task.status === 'ongoing' && !isProjectCompleted && (
                          <button
                            className="primary-btn px-2 sm:px-3 py-1 text-sm sm:text-base whitespace-nowrap"
                            onClick={() => openSubmissionModal(task.id)}
                          >
                            Send for Approval
                          </button>
                        )}
                        {user.role === 'manager' && !isProjectCompleted && (
                          <select
                            className="input-field p-1 sm:p-2 text-text text-sm sm:text-base"
                            value={task.status}
                            onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          >
                            <option value="ongoing">Ongoing</option>
                            <option value="completed">Completed</option>
                            <option value="pending_approval">Pending Approval</option>
                          </select>
                        )}
                        {isProjectCompleted && (
                          <span className="text-gray-500 text-sm">View Only (Project Completed)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {user.role === 'manager' && (
        <div className="card p-4 sm:p-6 mt-4 sm:mt-6">
          <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Pending Approvals</h2>
          {pendingApprovals.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">No tasks pending approval.</p>
          ) : (
            <div className="admin-table-shell overflow-x-auto">
              <table className="admin-table text-sm sm:text-base">
                <thead>
                  <tr className="soft-table-head">
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Title</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Assignee</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Project</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Major Task</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Due Date & Time</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Priority</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Submission</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Status</th>
                    <th className="p-2 sm:p-3 min-w-[220px] sm:min-w-[280px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPendingApprovals.map(task => {
                    const projectStatus = task.major_task?.project?.status;
                    const isProjectCompleted = projectStatus === 'completed';
                    const submittedFiles = (task.submitted_files && task.submitted_files.length > 0)
                      ? task.submitted_files
                      : (task.submitted_file ? [task.submitted_file] : []);
                    const submittedLinks = (task.submitted_links && task.submitted_links.length > 0)
                      ? task.submitted_links
                      : (task.submitted_link ? [task.submitted_link] : []);
                    return (
                      <tr key={task.id} className="border-b border-secondary table-row-hover">
                        <td className="p-2 sm:p-3 text-text">{task.name}</td>
                        <td className="p-2 sm:p-3 text-text">{task.assigned_to?.email || 'N/A'}</td>
                        <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || 'N/A'}</td>
                        <td className="p-2 sm:p-3 text-text">{task.major_task?.name || 'N/A'}</td>
                        <td className="p-2 sm:p-3 text-text">
                          {task.due_date ? formatDateTime24(task.due_date, locale) : 'N/A'}
                        </td>
                        <td className="p-2 sm:p-3 text-text">{task.priority}</td>
                        <td className="p-2 sm:p-3 text-text">
                          {submittedFiles.map((fileUrl, index) => (
                            <a
                              key={`submitted-file-${task.id}-${index}`}
                              href={normalizeUrl(fileUrl)}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline block"
                            >
                              Download File {index + 1}
                            </a>
                          ))}
                          {submittedLinks.map((linkUrl, index) => (
                            <a
                              key={`submitted-link-${task.id}-${index}`}
                              href={linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline block"
                            >
                              View Link {index + 1}
                            </a>
                          ))}
                          {submittedFiles.length === 0 && submittedLinks.length === 0 && '-'}
                        </td>
                        <td className="p-2 sm:p-3 text-text">
                          <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                        </td>
                        <td className="p-2 sm:p-3 text-text min-w-[220px] sm:min-w-[280px]">
                          {!isProjectCompleted ? (
                            <div className="flex flex-col gap-2">
                              <button
                                className="primary-btn px-4 py-2 text-center whitespace-nowrap"
                                onClick={() => handleStatusChange(task.id, 'completed')}
                              >
                                <FaCheck className="inline mr-2" /> Approve Task
                              </button>
                              <button
                                className="danger-btn px-4 py-2 text-center whitespace-nowrap"
                                onClick={() => handleDeclineTask(task.id)}
                              >
                                <FaTimes className="inline mr-2" /> Decline Task
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-500 text-sm">View Only (Project Completed)</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {submissionModalOpen && (
        <div className="overlay-backdrop fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="dialog-surface w-full max-w-md p-6">
            <h2 className="mb-4 text-xl font-semibold text-text">Submit Task</h2>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-text">Attach Files (Optional)</label>
              <input
                type="file"
                name="submitted_files"
                multiple
                onChange={handleSubmissionInputChange}
                className="input-field w-full p-2 text-sm"
              />
              <p className="mt-1 text-xs text-secondary-text">Max 5 files, total 25MB.</p>
              {submissionData.submitted_files.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-text">
                  {submissionData.submitted_files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="flex items-center justify-between">
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeSubmissionFile(index)}
                        className="text-xs text-error transition hover:opacity-80"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-text">Attach Links (Optional)</label>
                <button
                  type="button"
                  onClick={addSubmissionLink}
                  className="text-xs text-accent-blue transition hover:opacity-80"
                >
                  Add Link
                </button>
              </div>
              <p className="mb-2 text-xs text-secondary-text">Max 5 links.</p>
              {submissionData.submitted_links.length === 0 && (
                <p className="text-xs italic text-secondary-text">No links added.</p>
              )}
              <div className="space-y-2">
                {submissionData.submitted_links.map((link, index) => (
                  <div key={`submitted-link-${index}`} className="flex items-center space-x-2">
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => updateSubmissionLink(index, e.target.value)}
                      placeholder="https://example.com"
                      className="input-field w-full p-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeSubmissionLink(index)}
                      className="text-xs text-error transition hover:opacity-80"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setSubmissionModalOpen(false)}
                className="secondary-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSendForApproval}
                className="primary-btn flex items-center"
                disabled={uploading}
              >
                {uploading && (
                  <span className="mr-2 w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}
                {!uploading && uploadSuccess && <FaCheck className="mr-2" />}
                {uploading ? 'Uploading...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyTasks;
